import {DotLottieReact} from '@lottiefiles/dotlottie-react'
import {Breadcrumb, Breadcrumbs} from 'react-aria-components'

import ClockSvg from '@/assets/icons/clock.svg?react'
import EditSvg from '@/assets/icons/edit.svg?react'
import TrashSvg from '@/assets/icons/trash.svg?react'
import spinnerLottie from '@/assets/lotties/spinner.lottie'
import ProductPrice from '@/components/ProductCard/ProductPrice'
import QuantitySelector from '@/components/QuantitySelector/QuantitySelector'
import SkipLink from '@/components/SkipLink'
import Footer from '@/layout/Footer'
import Navbar from '@/layout/Navbar'
import type {FoodGrubhubCartItem} from '@/lib/food-grubhub/foodGrubhubCartDb'
import {GRUBHUB_ERROR_MESSAGE_KEYS} from '@/lib/food-grubhub/foodGrubhubErrors'
import {useFoodGrubhubCart} from '@/lib/food-grubhub/hooks/useFoodGrubhubCart'
import useFoodGrubhubCartMutation, {
  FoodGrubhubCartAction,
} from '@/lib/food-grubhub/hooks/useFoodGrubhubCartMutation'
import useGrubhubGetAuth from '@/lib/food-grubhub/hooks/useGrubhubGetAuth'
import useGrubhubMenuItemDetail from '@/lib/food-grubhub/hooks/useGrubhubMenuItemDetail'
import useRestaurantInformationDetail from '@/lib/food-grubhub/hooks/useRestaurantInformationDetail'
import {useGrubhubUserAddress} from '@/lib/food-grubhub/store'
import buildOptionsText from '@/lib/food-grubhub/utils/buildOptionsText'
import getPrice from '@/lib/food-grubhub/utils/getPrice'
import HeadTags from '@/lib/head/HeadTags'
import {useLocale} from '@/lib/locale/locale'
import {
  FoodGrubhubCartRoute,
  FoodGrubhubCategoryDetailRoute,
  FoodGrubhubDetailRoute,
  FoodGrubhubRoute,
} from '@/routeRegistry'
import skipTargetProps from '@/utils/a11y/skipTargetProps'
import VisuallyHidden from '@/utils/a11y/VisuallyHidden'
import {useIsMobile} from '@/utils/hooks/useWindowSize'
import RACLink from '@/utils/router/RACLink'

import style from './FoodGrubhubCart.module.scss'
import FoodGrubhubCartSummary from './FoodGrubhubCartSummary'

const FoodGrubhubCartItemComponent = memo<{
  item: FoodGrubhubCartItem
  isMobile: boolean
}>(function FoodGrubhubCartItemComponent({item, isMobile}) {
  const latestItem = useLatest(item)
  const {data: authResponse} = useGrubhubGetAuth()
  const [userAddress] = useGrubhubUserAddress()
  const timestampRef = useRef(Date.now())

  const categoryDetailQuery = {
    restaurantId: item.restaurantId,
    menuItemId: item.menuItemId,
    time: timestampRef.current,
    hideUnavailableMenuItems: true,
    orderType: 'standard',
    locationMode: 'DELIVERY',
    version: 4,
    includeWeightedItemsData: true,
    location: `POINT(${userAddress.longitude} ${userAddress.latitude})`,
  }

  const {data: menuItemDetail} = useGrubhubMenuItemDetail(
    categoryDetailQuery,
    authResponse?.session_handle.access_token ?? '',
  )

  console.log(menuItemDetail)

  const selectedOptionsText = buildOptionsText(menuItemDetail, item.options)
  const price = getPrice(menuItemDetail, item.options)

  const {mutate: mutateFoodGrubhubCart} = useFoodGrubhubCartMutation()

  const handleQuantityChange = useCallback(
    (q: number) => {
      mutateFoodGrubhubCart({
        action: FoodGrubhubCartAction.UpdateQuantity,
        data: {
          item: {
            menuItemId: latestItem.current.menuItemId,
            restaurantId: latestItem.current.restaurantId,
            options: latestItem.current.options,
            specialInstructions: latestItem.current.specialInstructions,
          },
          quantity: q,
        },
      })
    },
    [mutateFoodGrubhubCart],
  )

  const handleRemoveItem = useCallback(() => {
    mutateFoodGrubhubCart({
      action: FoodGrubhubCartAction.Remove,
      data: {
        item: {
          menuItemId: latestItem.current.menuItemId,
          restaurantId: latestItem.current.restaurantId,
          options: latestItem.current.options,
          specialInstructions: latestItem.current.specialInstructions,
        },
      },
    })
  }, [mutateFoodGrubhubCart])

  if (!menuItemDetail) return null

  if (isMobile) {
    return (
      <>
        <tr key={item.menuItemId}>
          {/* eslint-disable-next-line jsx-a11y/no-interactive-element-to-noninteractive-role -- this is okay */}
          <td role='presentation'>
            <div className={style.Item}>
              <Link
                to={FoodGrubhubCategoryDetailRoute.fullPath}
                params={{
                  restaurantId: item.restaurantId,
                  menuItemId: item.menuItemId,
                }}
                aria-hidden
              >
                {menuItemDetail.media_image && (
                  <img
                    src={`${menuItemDetail.media_image.base_url}${menuItemDetail.media_image.public_id}.${menuItemDetail.media_image.format}`}
                    alt={menuItemDetail.name ?? ''}
                  />
                )}
              </Link>
              <div className={style.ItemInfo}>
                <div role='cell'>
                  <VisuallyHidden as='div'>
                    {menuItemDetail.media_image && (
                      <img
                        src={`${menuItemDetail.media_image.base_url}${menuItemDetail.media_image.public_id}.${menuItemDetail.media_image.format}`}
                        alt={menuItemDetail.name ?? ''}
                      />
                    )}
                  </VisuallyHidden>
                  <Link
                    to={FoodGrubhubCategoryDetailRoute.fullPath}
                    params={{
                      restaurantId: item.restaurantId,
                      menuItemId: item.menuItemId,
                    }}
                    aria-hidden
                    className={style.ItemName}
                  >
                    {menuItemDetail.name}
                  </Link>
                  <div className={style.VariantOption}>
                    <div>{selectedOptionsText}</div>
                    {item.specialInstructions && <div>Notes: {item.specialInstructions}</div>}
                  </div>
                </div>
                <VisuallyHidden role='cell'>{menuItemDetail.name}</VisuallyHidden>
                <div aria-hidden>
                  <ProductPrice price={price.times(item.quantity).toNumber()} />
                </div>
                <div role='cell'>
                  <QuantitySelector
                    allowZero
                    quantity={item.quantity}
                    setQuantity={handleQuantityChange}
                  />
                </div>
                <VisuallyHidden role='cell'>
                  <ProductPrice price={price.times(item.quantity).toNumber()} />
                </VisuallyHidden>
                <div role='cell' className={style.ItemRemove}>
                  <button
                    className={style.RemoveItemBtn}
                    onClick={handleRemoveItem}
                    aria-label={`Remove ${menuItemDetail.name} from cart`}
                  >
                    <TrashSvg className={style.TrashIcon} />
                  </button>
                </div>
              </div>
            </div>
          </td>
        </tr>
        {/* <VisuallyHidden as='tr' strict>
          <td>
            <TRLink
              to='/product/$productId'
              params={{
                productId: {
                  retailer: item.retailer,
                  id: item.id,
                },
              }}
            >
              <img src={item.image} alt={item.title} />
            </TRLink>
            <TRLink
              to='/product/$productId'
              params={{
                productId: {
                  retailer: item.retailer,
                  id: item.id,
                },
              }}
            >
              {item.title}
            </TRLink>
          </td>
          <td>{item.seller ?? item.retailer}</td>
          <td>
            <QuantitySelector
              allowZero
              quantity={item.quantity}
              setQuantity={handleQuantityChange}
            />
          </td>
          <td>
            <ProductPrice price={item.price * item.quantity} />
          </td>
          <td>
            <button
              className={style.RemoveItemBtn}
              onClick={handleRemoveItem}
              aria-label={`Remove ${item.title} from cart`}
            >
              <TrashSvg className={style.TrashIcon} />
            </button>
          </td>
        </VisuallyHidden> */}
      </>
    )
  }

  return (
    <tr key={item.menuItemId}>
      <td>
        <div className={style.Item}>
          <Link
            to={FoodGrubhubCategoryDetailRoute.fullPath}
            params={{
              restaurantId: item.restaurantId,
              menuItemId: item.menuItemId,
            }}
            aria-hidden
          >
            {menuItemDetail.media_image && (
              <img
                src={`${menuItemDetail.media_image.base_url}${menuItemDetail.media_image.public_id}.${menuItemDetail.media_image.format}`}
                alt={menuItemDetail.name ?? ''}
              />
            )}
          </Link>
          <div className={style.ItemInfo}>
            <Link
              to={FoodGrubhubCategoryDetailRoute.fullPath}
              params={{
                restaurantId: item.restaurantId,
                menuItemId: item.menuItemId,
              }}
              aria-hidden
              className={style.ItemName}
            >
              {menuItemDetail.name}
            </Link>
            <div className={style.VariantOption}>
              <div>{selectedOptionsText}</div>
              {item.specialInstructions && <div>Notes: {item.specialInstructions}</div>}
            </div>
          </div>
        </div>
      </td>
      <VisuallyHidden as='td'>{menuItemDetail.name}</VisuallyHidden>
      <td>
        <QuantitySelector allowZero quantity={item.quantity} setQuantity={handleQuantityChange} />
      </td>
      <td>
        <ProductPrice price={price.times(item.quantity).toNumber()} />
      </td>
      <td>
        <button
          className={style.RemoveItemBtn}
          onClick={handleRemoveItem}
          aria-label={`Remove ${menuItemDetail.name} from cart`}
        >
          <TrashSvg className={style.TrashIcon} />
        </button>
      </td>
    </tr>
  )
})

export default memo(function Cart() {
  const isMobile = useIsMobile()
  const [locale] = useLocale()
  const {data: authResponse} = useGrubhubGetAuth()
  const {
    data: foodGrubhubCart,
    isLoading: isFoodGrubhubCartLoading,
    error: foodGrubhubCartError,
  } = useFoodGrubhubCart(locale)

  const [userAddress] = useGrubhubUserAddress()

  const restaurantId = foodGrubhubCart?.foodCartItems[0]?.restaurantId

  const restaurantDetailQuery = {
    restaurantId: restaurantId ?? null,
    hideChoiceCategories: true,
    version: 4,
    variationId: 'rtpFreeItems',
    orderType: '',
    hideUnavailableMenuItems: false,
    hideMenuItems: false,
    locationMode: 'delivery',
    location: `POINT(${userAddress.longitude} ${userAddress.latitude})`,
  }

  const {data: restaurantDetail, isLoading: restaurantDetailLoading} =
    useRestaurantInformationDetail(
      restaurantDetailQuery,
      authResponse?.session_handle.access_token ?? '',
    )

  console.log(restaurantDetail)

  const isLoading = restaurantDetailLoading && isFoodGrubhubCartLoading

  const cartItemNumber = foodGrubhubCart?.foodCartItems.length ?? 0

  const {coupon, applyRewards} = FoodGrubhubCartRoute.useSearch()

  const underlyingError = foodGrubhubCart?.moreInfo?.errorDetails
    ? (foodGrubhubCart.moreInfo.errorDetails.find(
        error => error.messageKey in GRUBHUB_ERROR_MESSAGE_KEYS,
      )?.messageKey ?? true)
    : undefined

  const underlyingErrorMessage = (() => {
    if (typeof underlyingError === 'string') {
      return GRUBHUB_ERROR_MESSAGE_KEYS[underlyingError]
    }

    if (underlyingError === true) {
      return 'Something went wrong, please contact us!'
    }

    return undefined
  })()

  const isEmpty =
    !isLoading && cartItemNumber === 0 && !foodGrubhubCartError && !underlyingErrorMessage

  console.log(foodGrubhubCart)

  // const {data: foodGrubhubCart, error} = useFoodGrubhubCartFull(locale)

  const userAddressString = userAddress.address

  return (
    <>
      <HeadTags title='Food Cart' />
      <SkipLink title='Skip to main content' to='#main-content' />
      <div className={style.Page}>
        <Navbar
          transparentBg
          withoutLogoOnDesktop
          variant='light'
          className={style.Navbar}
          haveCart={false}
        />
        <main className={style.CartPage} {...skipTargetProps('main-content')}>
          <div className={style.CartContent}>
            <div className={style.BreadcrumbsContainer} role='region' aria-label='breadcrumb'>
              <Breadcrumbs className={style.Breadcrumbs}>
                <Breadcrumb className={style.Breadcrumb}>
                  <RACLink
                    className={style.Link}
                    to={FoodGrubhubRoute.fullPath}
                    activeOptions={{
                      exact: true,
                    }}
                  >
                    Home
                  </RACLink>
                </Breadcrumb>
                <Breadcrumb className={style.Breadcrumb}>
                  <RACLink
                    className={style.Link}
                    to={FoodGrubhubCartRoute.fullPath}
                    search={{
                      applyRewards,
                      coupon,
                    }}
                  >
                    Food Cart
                  </RACLink>
                </Breadcrumb>
              </Breadcrumbs>
            </div>
            <h1 className={style.Heading}>Your food cart</h1>
            <div className={style.DeliveryInfo}>
              Delivery to: <span>{userAddressString}</span>
              <Link to={FoodGrubhubRoute.fullPath} aria-label='Update delivery address'>
                <EditSvg />
              </Link>
            </div>
            <div className={style.Cart}>
              <div className={style.CartItems}>
                <table className={style.CartTable}>
                  <caption>{`All items (${cartItemNumber} items)`}</caption>
                  <VisuallyHidden as='thead' isVisible={!isMobile}>
                    <tr>
                      <th style={{width: '100%'}}>
                        <VisuallyHidden>Item</VisuallyHidden>
                      </th>
                      <VisuallyHidden as='th'>Store</VisuallyHidden>
                      <th>Quantity</th>
                      <th>Price</th>
                      <VisuallyHidden as='th'>Actions</VisuallyHidden>
                    </tr>
                  </VisuallyHidden>
                  <tbody>
                    {isLoading && (
                      <tr>
                        <td colSpan={5}>
                          <div className={style.SpinnerContainer}>
                            <DotLottieReact
                              src={spinnerLottie}
                              autoplay
                              loop
                              className={style.Spinner}
                              aria-label='Loading product...'
                              role='progressbar'
                            />
                          </div>
                        </td>
                      </tr>
                    )}
                    {isEmpty && (
                      <tr>
                        <td colSpan={5}>
                          <div className={style.EmptyCart}>Your cart is empty</div>
                        </td>
                      </tr>
                    )}
                    {(foodGrubhubCartError || underlyingErrorMessage) && (
                      <tr>
                        <td colSpan={5}>
                          <div className={style.EmptyCart}>
                            {foodGrubhubCartError?.message ?? underlyingErrorMessage}
                          </div>
                        </td>
                      </tr>
                    )}
                    {!foodGrubhubCartError && !underlyingErrorMessage && (
                      <>
                        {!isLoading && !isEmpty && restaurantDetail && restaurantId && (
                          <tr aria-hidden>
                            <td colSpan={5}>
                              <div className={style.RetailerContainer}>
                                <Link
                                  className={style.RetailerLink}
                                  to={FoodGrubhubDetailRoute.fullPath}
                                  params={{
                                    restaurantId,
                                  }}
                                >
                                  <img src={restaurantDetail.restaurant.logo ?? ''} alt='' />
                                  {restaurantDetail.restaurant.name}
                                </Link>
                                {foodGrubhubCart.moreInfo?.orderingInfo?.estimateRange && (
                                  <div className={style.DeliveryTime}>
                                    <ClockSvg />
                                    <div>Est. Delivery</div>
                                    <div>
                                      {/* {scheduleId ? (
                                    expectedDeliveryDateRelative
                                  ) : (
                                    <> */}
                                      {foodGrubhubCart.moreInfo.orderingInfo.estimateRange.minimum}{' '}
                                      to{' '}
                                      {foodGrubhubCart.moreInfo.orderingInfo.estimateRange.maximum}{' '}
                                      mins
                                      {/* </>
                                  )} */}
                                    </div>
                                  </div>
                                )}
                                {/* {error && <div className={style.StoreError}>{error.message}</div>} */}
                                {/* TODO: hide & show products */}
                                {/* <button tabIndex={-1}>
                            <ChevronDownSvg aria-hidden />
                          </button> */}
                              </div>
                            </td>
                          </tr>
                        )}
                        {/* TODO: remove item animation */}
                        {!isLoading &&
                          !isEmpty &&
                          restaurantId &&
                          foodGrubhubCart.foodCartItems.map(item => {
                            return (
                              <FoodGrubhubCartItemComponent
                                key={item.menuItemId}
                                item={item}
                                isMobile={isMobile}
                              />
                            )
                          })}
                      </>
                    )}
                  </tbody>
                </table>
              </div>
              {!isMobile && <FoodGrubhubCartSummary />}
            </div>
          </div>
        </main>
        {isMobile && <FoodGrubhubCartSummary />}
        <Footer />
      </div>
    </>
  )
})
